
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { MAIN_URL } from "../../../Configurations/Urls";
import toast from "react-hot-toast";

import { formatDate, formatTime } from "../../../Utils/DateAndTime";

import { useNavigate, useSearchParams } from "react-router-dom";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import Layout4 from "../../DataLayouts/Layout4";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { isValidFilterValue } from "../../../Components/filters/FilterService";
import {
  buildListRequestBody,
  getListAuthHeaders,
  pruneStaleFilterSearchParams,
  applyDefaultSearchParams,
} from "../utils/listFilters";
import useModuleListConfig from "../../../Hooks/useModuleListConfig";

const FILTER_MODULE = "organization_project_tasks";

function InternProjectTasksList() {
  const navigate = useNavigate();
  const { userData, reporting_manager_interns } = useAuthStore();
  const orgId =
    userData?.organization?.organization_id ??
    userData?.user?.organization_id ??
    userData?.organization_id ??
    null;
  const [searchParams, setSearchParams] = useSearchParams();
  const { config: listConfig } = useModuleListConfig(FILTER_MODULE, "intern");

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const appliedFiltersRef = useRef([]);

  const CopyFunction = async (id) => {
    const loadingToast = toast.loading("Copying the task, please wait...");

    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(
        `${MAIN_URL}/api/project-management/project-tasks/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = res.data?.data;

      if (data) {
        let finalStructured = {
          id: data?.organization_project_task_id,

          organization_id: data.organization_id || "",
          organization_entity_id: data.organization_entity_id || "",
          organization_project_id: data.organization_project_id || "",
          organization_project_milestone_id: data.organization_project_milestone_id || "",
          organization_project_task_template_id: data.organization_project_task_template_id || "",
          organization_project_task_category_id: data.organization_project_task_category_id || "",
          organization_project_task_subcategory_id: data.organization_project_task_subcategory_id || "",
          priority: data.priority || "",
          task_title: data.task_title || "",
          parent_task_id: data.parent_task_id || "",
          organization_project_task_type_id: data.organization_project_task_type_id || "",
          complexity_level: data.complexity_level || "",
          quantity: data.quantity || "",
          estimated_minutes: data.estimated_minutes || "",
          assigned_employee_id: data.assigned_employee_id || "",
          scheduled_date: formatDate(data.scheduled_date) || "",
          scheduled_time: formatTime(data.scheduled_time) || "",
          status_remarks: data.status_remarks || "",
          schedules: (data.schedules || []).map((sc) => ({
            organization_project_task_schedule_id: null,
            organization_id: sc.organization_id || "",
            organization_entity_id: null,
            organization_project_task_id: sc.organization_project_task_id || null,
            scheduled_date: (sc.scheduled_date || "").split("T")[0] || null,
            allocated_minutes: sc.allocated_minutes,
            is_reassigned: sc.is_reassigned,
            task_status: sc.task_status,
            status_remarks: sc.status_remarks,
          })),
        };

        toast.dismiss(loadingToast);
        toast.success("Task copied successfully!.");

        navigate("/project-task/interns/add", { state: { copiedTask: finalStructured } });
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error("Failed to fetch project task.");
    }
  };

  const ViewFunction = (obj) => {
    navigate(`/project-task/interns/view/${obj?.id}`);
  };

  const deleteTask = async (id) => {
    await axios.delete(
      `${MAIN_URL}/api/project-management/project-tasks/${id}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
  };

  // Initialize URL parameters with defaults on mount
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const { params: currentParams, needsUpdate } = applyDefaultSearchParams(
      searchParams,
      listConfig
    );

    if (needsUpdate) {
      setSearchParams(currentParams, { replace: true });
    }

    hasInitializedURL.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasInitializedURL.current) return;
    const { params, needsUpdate } = pruneStaleFilterSearchParams(searchParams);
    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Load datagrid configuration (intern-specific grid key)
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!orgId) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: getListAuthHeaders(),
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `Intern_tasks_grid_${orgId}`;
          const savedConfig = datagrids.find((dg) => dg.datagrid_key === orgKey);

          if (savedConfig?.datagrid_default_configuration) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);
            if (Array.isArray(serverCfg.columns) && serverCfg.columns.length > 0) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(null);
            }
          } else {
            setConfigColumns(null);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(null);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [orgId]);

  const fetchInternProjectTasksWithFilters = useCallback(async () => {
    if (!orgId) return;

    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }
    fetchControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const extra =
        Array.isArray(reporting_manager_interns) &&
        reporting_manager_interns.length > 0
          ? { organization_person_ids: reporting_manager_interns }
          : {};

      const requestBody = buildListRequestBody({
        searchParams,
        orgId,
        listConfig,
        appliedFiltersRef,
        extra,
      });

      const filteredUrl =
        listConfig.filteredEndpoint ||
        "/api/project-management/project-tasks/filtered";

      const response = await fetch(
        `${MAIN_URL}${filteredUrl}`,
        {
          method: "POST",
          headers: {
            ...getListAuthHeaders(),
          },
          body: JSON.stringify(requestBody),
          signal: fetchControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        const normalized = result.data.map((item) => ({
          ...item,
          id: item.organization_project_task_id ?? item.id,
          project_task_name: item.project_task_name ?? item.task_title,
          project: item.project ?? item.organization_project_id,
          task_category:
            item.task_category ?? item.organization_project_task_category_id,
          task_sub_category:
            item.task_sub_category ??
            item.organization_project_task_subcategory_id,
          assigned_to_employee:
            item.assigned_to_employee ?? item.assigned_organization_person_id,
          assigned_employee:
            item.assigned_employee ??
            item.assigned_to_employee ??
            item.assigned_organization_person_id,
          assigned_employ:
            item.assigned_employ ??
            item.assigned_employee ??
            item.assigned_to_employee ??
            item.assigned_organization_person_id,
          assigned_employe:
            item.assigned_employe ??
            item.assigned_employee ??
            item.assigned_to_employee ??
            item.assigned_organization_person_id,

          project_milestone:
            item.project_milestone ?? item.organization_project_milestone_id,
          milestone:
            item.milestone ??
            item.project_milestone ??
            item.organization_project_milestone_id,
          parent_task: item.parent_task ?? item.parent_task_id,
          task_type: item.task_type ?? item.organization_project_task_type_id,
          complexity: item.complexity ?? item.complexity_level,
          allocated_minutes:
            item.allocated_minutes ??
            item.total_estimated_minutes ??
            item.estimated_minutes,

          template:
            item.template ?? item.organization_project_task_template_id,
          task_template:
            item.task_template ?? item.organization_project_task_template_id,
          team: item.team ?? item.organization_project_team_id,
          project_team: item.project_team ?? item.organization_project_team_id,

          organization_person:
            item.organization_person ?? item.assigned_organization_person_id,
          organization_person_id:
            item.organization_person_id ?? item.assigned_organization_person_id,

          total_calculated_minutes:
            item.total_calculated_minutes ??
            item.actual_minutes_taken ??
            0,

          assigned_employee_id:
            item.assigned_employee_id ?? item.assigned_organization_person_id,
          organization_persons:
            item.organization_persons ?? item.assigned_organization_person_id,
          status_remark:
            item.status_remark ?? item.status_remarks ?? item.task_remarks,
          statusRemarks:
            item.statusRemarks ?? item.status_remarks ?? item.task_remarks,
        }));
        setTasks(normalized);

        if (result.pagination) {
          const pageSize = result.pagination.pageSize || result.pagination.limit || 10;
          const requestedPage = Number.parseInt(searchParams.get("page"), 10) || 1;
          setPaginationData({
            page: result.pagination.page || requestedPage,
            pageSize,
            total: result.pagination.total || 0,
            totalPages: result.pagination.totalPages || 1,
            hasNext: result.pagination.hasNext || false,
            hasPrev: result.pagination.hasPrev || false,
          });
        } else {
          setPaginationData({
            page: 1,
            pageSize: 10,
            total: normalized.length,
            totalPages: 1,
            hasNext: false,
            hasPrev: false,
          });
        }
      } else {
        setTasks([]);
        setPaginationData({
          page: 1,
          pageSize: 10,
          total: 0,
          totalPages: 1,
          hasNext: false,
          hasPrev: false,
        });
        toast.error(result.message || result.error || "Failed to fetch intern project tasks");
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("Error fetching intern project tasks:", err);
      toast.error("Failed to fetch intern project tasks");
      setTasks([]);
      setPaginationData({
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 1,
        hasNext: false,
        hasPrev: false,
      });
    } finally {
      setLoading(false);
    }
  }, [orgId, reporting_manager_interns, searchParams, listConfig]);

  useEffect(() => {
    fetchInternProjectTasksWithFilters();
    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [fetchInternProjectTasksWithFilters]);

  const handleFilterChange = useCallback((builderFilters) => {
    if (!Array.isArray(builderFilters)) {
      appliedFiltersRef.current = [];
      return;
    }
    appliedFiltersRef.current = builderFilters.filter(
      (f) =>
        f?.field &&
        f?.operator &&
        (!operatorRequiresValue(f.operator) || isValidFilterValue(f.value))
    );
  }, []);

  return (
    <>
      <Layout4
        heading="Intern Project Tasks"
        Route="/project-task/interns"
        btnName="Add Intern Project Task"
        showAddButton={true}
        add_action="ADD_INTERN_TASK"
      />

      {loading || loadingConfig ? (
        <div style={{ padding: 16 }}>Loading...</div>
      ) : (
        <TableDataGeneric
          onFilterChange={handleFilterChange}
          copyFunction={(obj) => CopyFunction(obj?.id)}
          copy_action="COPY_INTERN_TASK"
          viewFunction={(obj) => ViewFunction(obj)}
          gridKey="Intern_tasks_grid"
          tableName="organization_project_tasks"
          filterModule={FILTER_MODULE}
          primaryKey="id"
          heading="organization_project_tasks"
          data={tasks}
          sortname={"scheduled_date"}
          showActions={true}
          Route="/project-task/interns"
          DeleteFunc={deleteTask}
          token={localStorage.getItem("token")}
          configss={configColumns}
          edit_delete_action={["EDIT_INTERN_TASK", "DELETE_INTERN_TASK"]}
          paginationData={paginationData}
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default InternProjectTasksList;
